import { downloadAsJpeg } from 'Cloud/Application/DownloadAsJpeg';
import { ReactComponent as FullScreenIcon } from 'img/icons/fullScreen.svg';
import { ReactComponent as CloudIcon } from 'mrg-icons/src/mailru/categories/cloud.svg';
import React, { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { goToRegisterPage } from 'reactApp/modules/ph/ph.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { Button } from 'reactApp/ui/Button/Button';
import { useCallbacks } from 'reactApp/ui/Mobile/OptionsModal/hooks/useCallbacks';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ViewerShareButton } from 'reactApp/ui/ViewerShareButton/ViewerShareButton';
import { sendGaAb } from 'reactApp/ui/ViewerToolbar/ViewerToolbar.helpers';
import type { IFile } from 'reactApp/ui/ViewerToolbar/ViewerToolbar.types';
import { ViewerToolbarMobile } from 'reactApp/ui/ViewerToolbar/ViewerToolbarMobile';
import { formatDate, formatExpires } from 'reactApp/utils/formatDate';
import { sendXray } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';

import styles from './ViewerToolbar.css';

interface IProps {
    isAuthorized: boolean;
    file: IFile;
    onSave: () => void;
    onClone: () => void;
    onLogin: () => void;
    onShare: () => void;
    socialList: any[];
    onSocial: (id: string) => void;
    isIOS: boolean;
    onDownload: (useDownloadUrl: boolean) => void;
    canClone: boolean;
    onFullScreen: () => void;
    isPhone: boolean;
    isMobile: boolean;
    showFullScreen: boolean;
    sendGaRadar: (action: string) => void;
    isPreviewableItem?: boolean;
}

export const ViewerToolbar = memo<IProps>(
    // eslint-disable-next-line max-lines-per-function
    ({
        isPhone,
        file,
        sendGaRadar = noop,
        isAuthorized,
        onSocial,
        onLogin,
        onClone,
        isPreviewableItem,
        canClone,
        socialList,
        isMobile,
        showFullScreen = true,
        onFullScreen,
    }) => {
        const storage = useSelector(getCurrentStorage);
        const date = file.date ? formatDate(file.date) : '';
        const expires = file.expires ? formatExpires(file.expires) : '';
        const showCloneBtn = canClone || !isAuthorized;
        const theme = showCloneBtn ? '' : 'octavius';
        const color = showCloneBtn ? 'black' : undefined;

        useEffect(() => {
            if (isPhone && !IS_WEBVIEW) {
                sendXray(['public-file-view', 'ab-appdd', 'blue']);
                sendGaAb('blu', 'show');
            }

            if (downloadAsJpeg.isAvailable({ ext: file.extension })) {
                downloadAsJpeg.sendGa({ event: 'show', label: 'public' });
            }
        }, []);

        const sendGA = (action) => {
            sendXray(['publ-viewic-file', action, isAuthorized ? 'user' : 'anon']);

            sendGaRadar(action);
        };

        const handleOnSocial = (id) => {
            sendGA('social-click');

            onSocial(id);
        };

        const handleOnClone = () => {
            const eventAction = isAuthorized ? 'clone-click' : 'auth-click';

            sendGA(eventAction);

            // 'public-file' - общая категория событий для белого и черного одиночного паблика
            sendXray(['public-file', eventAction]);

            if (isAuthorized) {
                onClone();
            } else if (isPhone) {
                onLogin();
            } else {
                goToRegisterPage();
            }
        };

        const { onDownload: onDownloadHook } = useCallbacks({
            storage,
            id: file.id,
            onClose: handleOnClone,
            sendAnalytics: sendGA,
        });

        const handleOnDownload = () => {
            const eventAction = 'download-click';
            sendGA(eventAction);

            // 'public-file' - общая категория событий для белого и черного одиночного паблика
            sendXray(['public-file', eventAction]);

            onDownloadHook();
        };

        if (isPhone) {
            return (
                <ViewerToolbarMobile
                    file={file}
                    date={date}
                    expires={expires}
                    isPreviewableItem={isPreviewableItem}
                    canClone={canClone}
                    socialList={socialList}
                    handleOnClone={handleOnClone}
                    handleOnDownload={handleOnDownload}
                    handleOnSocial={handleOnSocial}
                />
            );
        }

        return (
            <div className={styles.root}>
                <div className={styles.left}>
                    {canClone && (
                        <div className={styles.action}>
                            <Button primary onClick={handleOnClone} size="big" data-name="actionWithCloud" theme="octavius">
                                <CloudIcon className={styles.buttonIcon} fill="currentColor" />
                                {isAuthorized ? 'Сохранить в Облако' : 'Завести Облако'}
                            </Button>
                        </div>
                    )}
                    <div className={styles.download}>
                        {downloadAsJpeg.isAvailable({ ext: file.extension }) ? (
                            <>
                                <Button
                                    primary={!showCloneBtn}
                                    onClick={handleOnDownload}
                                    color={color}
                                    data-name="downloadAsJpeg"
                                    theme={theme}
                                >
                                    Скачать в jpeg
                                </Button>
                                <Button onClick={handleOnDownload} color="black" data-name="downloadOriginal">
                                    Скачать в {file.extension.toLowerCase()}
                                </Button>
                            </>
                        ) : (
                            <Button primary={!showCloneBtn} onClick={handleOnDownload} color={color} data-name="download" theme={theme}>
                                Скачать
                            </Button>
                        )}
                    </div>
                    <div className={styles.info}>
                        <div className={styles.name}>
                            <NameComponent name={file.name} extension={file.extension} />
                        </div>
                        <div className={styles.meta}>
                            {file.size}
                            {date ? `, ${date}` : ''}
                        </div>
                    </div>
                </div>
                <div className={styles.right}>
                    {expires && (
                        <div className={styles.expires}>
                            Доступ по ссылке открыт
                            <br />
                            до {expires}
                        </div>
                    )}

                    {!isMobile && showFullScreen && (
                        <div className={styles.action}>
                            <Button color="black" onClick={onFullScreen}>
                                <FullScreenIcon />
                            </Button>
                        </div>
                    )}
                    <ViewerShareButton
                        onSocial={handleOnSocial}
                        socialList={socialList}
                        placement={tooltipPlacements.TOP_LEFT}
                        sendGaRadar={sendGaRadar}
                    />
                </div>
            </div>
        );
    }
);

ViewerToolbar.displayName = 'ViewerToolbar';
